import { CompanyModule } from '../enums/companyModule.enum'
import { CompanyPlan } from '../enums/companyPlan.enum'
import { IModuleAccess, ICompanySettings } from '../models/Company'
import {
  PERFORMANCE_MANAGEMENT_REQUEST_TYPES,
  RequestType,
} from '../models/Request'

// Default module-access shape: the original modules are on, newer modules start off
// until support enables them. The single source for the default, reused by the
// support backfill and the migration so the default can't drift across places.
export const DEFAULT_MODULE_ACCESS: IModuleAccess = {
  employerBranding: true,
  onboarding: true,
  performanceManagement: true,
  engagement: false,
  journals: true,
}

// A company without a stored plan predates the plans and has the full suite
export const getCompanyPlan = (
  settings: ICompanySettings | undefined
): CompanyPlan => settings?.plan ?? CompanyPlan.SUITE

// The modules a core company never has, whatever is stored for them
export const SUITE_MODULES = [
  CompanyModule.PERFORMANCE_MANAGEMENT,
  CompanyModule.ONBOARDING,
  CompanyModule.ENGAGEMENT,
]

// Single source of truth for the "absent = default" semantic. An absent
// settings/modules/key falls back to DEFAULT_MODULE_ACCESS, so existing companies
// (pre-migration) keep the original modules visible while engagement stays off.
// Takes settings (not a company) so both a lean backend ICompanyPure and a frontend
// ICompany can call it. On core the suite modules are off; what is stored for them
// stays, so moving back to suite restores it. Journals are part of performance
// management, so they are off whenever that is (and with it, on core).
export const isModuleEnabled = (
  settings: ICompanySettings | undefined,
  module: CompanyModule
): boolean =>
  (module !== CompanyModule.JOURNALS ||
    isModuleEnabled(settings, CompanyModule.PERFORMANCE_MANAGEMENT)) &&
  (!SUITE_MODULES.includes(module) ||
    getCompanyPlan(settings) === CompanyPlan.SUITE) &&
  (settings?.modules?.[module] ?? DEFAULT_MODULE_ACCESS[module])

// Whether a request of the given type should be surfaced for a company, based on
// its enabled modules: performance-management request types need the PM module,
// START_ONBOARDING needs the onboarding module, everything else is always shown
// (surveys are on every plan). Single source for this rule (request
// badges/lists, weekly summaries, reminder emails) so the call sites can't drift.
export const isRequestTypeModuleEnabled = (
  settings: ICompanySettings | undefined,
  actionType: RequestType
): boolean => {
  if (PERFORMANCE_MANAGEMENT_REQUEST_TYPES.includes(actionType)) {
    return isModuleEnabled(settings, CompanyModule.PERFORMANCE_MANAGEMENT)
  }
  if (actionType === RequestType.START_ONBOARDING) {
    return isModuleEnabled(settings, CompanyModule.ONBOARDING)
  }
  return true
}
